import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Download, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { format } from "date-fns";

const fmt = (v, dp = 2) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(dp) : String(v);
};

// Logical column keys (match backend /api/energy/readings output)
const COLS = [
  { k: "Time", l: "TIME", fmt: (v) => { try { return format(new Date(v), "dd MMM HH:mm:ss"); } catch { return v ?? ""; } } },
  { k: "vrn", l: "V1N",   fmt: (v) => fmt(v, 1) },
  { k: "vyn", l: "V2N",   fmt: (v) => fmt(v, 1) },
  { k: "vbn", l: "V3N",   fmt: (v) => fmt(v, 1) },
  { k: "irn", l: "I1",    fmt: (v) => fmt(v, 2) },
  { k: "iyn", l: "I2",    fmt: (v) => fmt(v, 2) },
  { k: "ibn", l: "I3",    fmt: (v) => fmt(v, 2) },
  { k: "ttl_kw",     l: "kW",       fmt: (v) => fmt(v, 2) },
  { k: "ttl_kva",    l: "kVA",      fmt: (v) => fmt(v, 2) },
  { k: "Average_pf", l: "PF",       fmt: (v) => fmt(v, 3) },
  { k: "Frequency",  l: "Hz",       fmt: (v) => fmt(v, 2) },
  { k: "kwh_net",    l: "kWh NET",  fmt: (v) => fmt(v, 1) },
];

export default function ReadingsTable({ startIso, endIso }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [localStart, setLocalStart] = useState("");
  const [localEnd, setLocalEnd] = useState("");
  const [active, setActive] = useState(null);

  const effStart = active?.start || startIso;
  const effEnd = active?.end || endIso;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/energy/readings`, {
        params: { start: effStart, end: effEnd, page, page_size: pageSize },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } finally { setLoading(false); }
  }, [effStart, effEnd, page, pageSize]);

  useEffect(() => { setPage(1); }, [effStart, effEnd]);
  useEffect(() => { load(); }, [load]);

  const applyLocal = () => {
    if (!localStart && !localEnd) return;
    const s = localStart ? new Date(localStart).toISOString() : startIso;
    const e = localEnd ? new Date(localEnd).toISOString() : endIso;
    setActive({ start: s, end: e });
  };
  const clearLocal = () => { setActive(null); setLocalStart(""); setLocalEnd(""); };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/energy/export`, {
      params: { start: effStart, end: effEnd },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "energy_readings.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="panel p-5" data-testid="readings-table">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-base tracking-tight">READINGS</h3>
          <div className="label-tech mt-1">
            {total.toLocaleString()} records · page {page}/{pages}
            {active ? <span className="text-cyan-400 ml-2">· LOCAL FILTER</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            data-testid="readings-start-input"
            type="datetime-local"
            value={localStart}
            onChange={(e) => setLocalStart(e.target.value)}
            className="bg-white/5 border-white/15 rounded-sm h-9 w-44 text-xs"
          />
          <Input
            data-testid="readings-end-input"
            type="datetime-local"
            value={localEnd}
            onChange={(e) => setLocalEnd(e.target.value)}
            className="bg-white/5 border-white/15 rounded-sm h-9 w-44 text-xs"
          />
          <Button data-testid="readings-search-btn" onClick={applyLocal} variant="outline"
                  className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-9">
            <Search className="w-3 h-3 mr-2" /> SEARCH
          </Button>
          {active && (
            <Button data-testid="readings-clear-btn" onClick={clearLocal} variant="ghost"
                    className="text-zinc-400 hover:text-white rounded-sm h-9">
              <X className="w-3 h-3" />
            </Button>
          )}
          <Button data-testid="export-csv-btn" onClick={exportCsv} variant="outline"
                  className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-9">
            <Download className="w-3 h-3 mr-2" /> EXPORT CSV
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-sm border border-white/10">
        <Table>
          <TableHeader>
            <TableRow className="bg-white/[0.02] border-white/10">
              {COLS.map((c) => (
                <TableHead key={c.k} className="label-tech text-zinc-400">{c.l}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={COLS.length} className="text-center text-zinc-500 py-8">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={COLS.length} className="text-center text-zinc-500 py-8">No data in range</TableCell></TableRow>
            ) : items.map((r, i) => (
              <TableRow key={r.id || i} className="border-white/10 hover:bg-white/[0.02]">
                {COLS.map((c) => (
                  <TableCell key={c.k} className="font-mono-tech text-xs">{c.fmt(r[c.k])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button data-testid="readings-prev-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                variant="outline" className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-8 px-3">
          <ChevronLeft className="w-3 h-3" />
        </Button>
        <Button data-testid="readings-next-btn" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}
                variant="outline" className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-8 px-3">
          <ChevronRight className="w-3 h-3" />
        </Button>
      </div>
    </section>
  );
}
